import { useCallback, useEffect, useRef, useState } from 'react';

export type ScrollSpySection = {
  id: string;
  label: string;
};

/** Section active selon la position de scroll (offset = hauteur header). */
export function useScrollSpy(sections: ScrollSpySection[], offset = 140) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');
  const activeIdRef = useRef(activeId);
  const rafRef = useRef(0);
  const sectionsRef = useRef(sections);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const resolveActive = useCallback(() => {
    const list = sectionsRef.current;
    if (list.length === 0) return;

    const scrollMarker = window.scrollY + offset;
    let current = list[0].id;

    for (const section of list) {
      const el = document.getElementById(section.id);
      if (el && el.offsetTop <= scrollMarker) {
        current = section.id;
      }
    }

    if (current !== activeIdRef.current) {
      activeIdRef.current = current;
      setActiveId(current);
    }
  }, [offset]);

  useEffect(() => {
    resolveActive();

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(resolveActive);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resolveActive);

    // Sections lazy-mountées : recalcul après chargement différé
    const lateCheck = window.setTimeout(resolveActive, 600);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.clearTimeout(lateCheck);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resolveActive);
    };
  }, [resolveActive]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: prefersReduced ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  return { activeId, scrollToSection };
}
